<template>
    <div class="datepicker-wrapper">
        <q-input
            outlined
            dense
            v-bind="$attrs"
            v-model="displayDate"
            :label="label"
            :readonly="readonly"
            hide-bottom-space
            :required="required"
            :mask="inputMask"
            :error="error"
            :error-message="errorMessage"
        >
            <template v-slot:append>
                <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                        ref="qDateProxy"
                        cover
                        transition-show="scale"
                        transition-hide="scale"
                    >
                        <q-date
                            v-model="displayDate"
                            minimal
                            :mask="dateFormat"
                            :options="options"
                            :locale="locale"
                            @update:model-value="() => qDateProxy.hide()"
                        >
                            <div class="row items-center justify-end">
                                <q-btn
                                    v-close-popup
                                    :label="closeLabel"
                                    color="primary"
                                    flat
                                />
                            </div>
                        </q-date>
                    </q-popup-proxy>
                </q-icon>
                <help-icon
                    v-if="helpText"
                    :text="helpText"
                    class="q-ml-xs text-grey"
                />
            </template>
        </q-input>
        <input
            v-if="name"
            type="hidden"
            :name="name"
            :value="internalValue"
            @input="internalValue = $event.target.value"
            @change="internalValue = $event.target.value"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export default {
    name: "DatepickerWrapper",
    props: {
        modelValue: String,
        name: String,
        label: String,
        required: Boolean,
        readonly: Boolean,
        error: Boolean,
        errorMessage: String,
        min: String,
        max: String,
        helpText: String,
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const qDateProxy = ref(null);
        const closeLabel = ref("Close");
        const locale = ref({});

        const currentLocale = window.locale || "en_US";

        const getDateFormat = (loc) => {
            if (loc.startsWith("en")) return "MM/DD/YYYY";
            if (loc.startsWith("zh") || loc.startsWith("ar"))
                return "YYYY/MM/DD";
            if (
                loc.startsWith("fr") ||
                loc.startsWith("es") ||
                loc.startsWith("it") ||
                loc.startsWith("pt") ||
                loc.startsWith("ro")
            ) {
                return "DD/MM/YYYY";
            }
            return "DD.MM.YYYY";
        };

        const dateFormat = getDateFormat(currentLocale);
        const inputMask = dateFormat.replace(/[DMY]/g, "#");

        const normalize = (val) => {
            if (!val) return "";
            // Try parsing from standard formats
            const d = dayjs(val, [
                "DD.MM.YYYY",
                "YYYY-MM-DD",
                "DD.MM.YY",
                "YYYY-MM-DD HH:mm:ss",
            ]);
            return d.isValid() ? d.format("DD.MM.YYYY") : val;
        };

        const internalValue = ref(normalize(props.modelValue));

        watch(
            () => props.modelValue,
            (newVal) => {
                const normalized = normalize(newVal);
                if (normalized !== internalValue.value) {
                    internalValue.value = normalized;
                }
            },
        );

        const displayDate = computed({
            get: () => {
                if (!internalValue.value) return "";
                const d = dayjs(internalValue.value, "DD.MM.YYYY");
                return d.isValid() ? d.format(dateFormat) : internalValue.value;
            },
            set: (val) => {
                if (!val) {
                    internalValue.value = "";
                    emit("update:modelValue", "");
                    return;
                }
                const d = dayjs(val, dateFormat);
                if (d.isValid()) {
                    const normalized = d.format("DD.MM.YYYY");
                    internalValue.value = normalized;
                    emit("update:modelValue", normalized);
                }
            },
        });

        const updateTranslations = () => {
            if (window.translations && window.translations.close) {
                closeLabel.value = window.translations.close;
            }
            if (window.monthNames && window.dayNames) {
                locale.value = {
                    days: window.dayNames,
                    daysShort: window.dayNamesMin,
                    months: window.monthNames,
                    monthsShort: window.monthNamesShort,
                    firstDayOfWeek: currentLocale.startsWith("en") ? 0 : 1,
                };
            }
        };

        onMounted(() => {
            updateTranslations();
            document.addEventListener(
                "translations-loaded",
                updateTranslations,
            );
        });

        const options = (date) => {
            if (!date) return false;
            // date is YYYY/MM/DD from Quasar
            const d = dayjs(date, "YYYY/MM/DD");

            if (props.min) {
                const min = dayjs(props.min, [
                    "YYYY/MM/DD",
                    "YYYY-MM-DD",
                    "DD.MM.YYYY",
                ]);
                if (min.isValid() && d.isBefore(min, "day")) return false;
            }
            if (props.max) {
                const max = dayjs(props.max, [
                    "YYYY/MM/DD",
                    "YYYY-MM-DD",
                    "DD.MM.YYYY",
                ]);
                if (max.isValid() && d.isAfter(max, "day")) return false;
            }
            return true;
        };

        return {
            qDateProxy,
            displayDate,
            internalValue,
            dateFormat,
            inputMask,
            closeLabel,
            options,
            locale,
        };
    },
};
</script>
